import { FC } from "react";

import {
  CategoryTreeObject,
  LinkedCategory,
} from "@vviinn/user-managment-client-ts";
import { X } from "lucide-react";

import { capitalizeAssociatedPress } from "../../helpers/string/capitalize";
import { VoidFunc } from "../../helpers/functions/types";
import { findByCode } from "./helpers/findByCode";
import { Button } from "../../ui/layout/molecules/Button";
import {
  DEFAULT_ICON_COLORS,
  LARGE_ICON_SIZE_STYLES,
  SMALL_ICON_SIZE_STYLES,
} from "../../ui/styles";
import { Badge } from "../../ui/layout/molecules/Badge/Badge";

const ITEM_PRIMARY_STYLES = `py-1 px-2 items-center max-w-fit text-xs rounded-lg bg-stone-100 border-stone-900 dark:border dark:bg-transparent relative cursor-default truncate text-stone-400 dark:text-stone-500`;

export type AssignedCategoriesProps = {
  assignedCategories: LinkedCategory[];
  list: CategoryTreeObject;
  isSourceDataStyle?: boolean;
  onUnassign?: VoidFunc<CategoryTreeObject>;
};

export const AssignedCategories: FC<AssignedCategoriesProps> = ({
  assignedCategories,
  list,
  isSourceDataStyle = true,
  onUnassign,
}) => {
  if (!assignedCategories || !list) return null;

  const ITEM_STYLES = `
   ${ITEM_PRIMARY_STYLES}}  ${onUnassign ? "pr-[30px] pl-3 py-2.5" : ""}`;

  const UnassignButton = (categoryTree: CategoryTreeObject) => {
    if (!onUnassign) return null;

    return (
      <Button
        title="unassign category"
        onClick={() => onUnassign(categoryTree)}
        buttonType="iconOnly"
        className={`${LARGE_ICON_SIZE_STYLES} ${DEFAULT_ICON_COLORS} absolute right-1 top-1.5 p-1 hover:text-stone-500 dark:hover:text-stone-300`}
      >
        <X className={SMALL_ICON_SIZE_STYLES} />
      </Button>
    );
  };

  const categories = assignedCategories?.map((category, index) => {
    const code = category.code;
    if (!code) return null;
    const categoryData = findByCode(code, list);
    if (!categoryData) {
      return onUnassign ? (
        <span key={index} className={ITEM_STYLES} title={code}>
          {UnassignButton({ categoryCode: code, title: "", tree: [] })}
          {code}
        </span>
      ) : (
        <Badge key={index} type="warning" title="Unknown category code" />
      );
    }

    const categoryTrees = isSourceDataStyle
      ? categoryData[1]
      : categoryData[1].reverse();
    const categoriesAmount = categoryTrees.length;

    const categoryElements = categoryTrees.map((category, index) => {
      const isFirst = index === 0;
      const isLast = index === categoriesAmount - 1;

      if ((isFirst && !isSourceDataStyle) || (isLast && isSourceDataStyle))
        return (
          <span key={index} className="text-stone-700 dark:text-stone-300">
            {capitalizeAssociatedPress(category.title)}
          </span>
        );

      return (
        <span key={index} className="ml-0.5">{`${
          isSourceDataStyle ? "" : " < "
        }${capitalizeAssociatedPress(category.title)}${
          isSourceDataStyle ? " > " : ""
        }`}</span>
      );
    });

    const categoriesText = categoryElements.reduce((acc, curr) => {
      return acc + curr.props.children;
    }, "");

    return (
      <span
        key={index}
        className={`${isSourceDataStyle && "direction-rtl"} ${ITEM_STYLES}`}
        title={categoriesText}
      >
        {onUnassign && UnassignButton(categoryData[0])}

        {categoryElements}
      </span>
    );
  });

  return <>{categories}</>;
};
